import _ from 'underscore'
import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'

import GA4 from 'helpers/ga4'

import LegendGroupItem from 'pages/Device/AwairRthPanel/LegendGroupItem'

import { color } from 'styles/colors'
import { AwairLegendItems, SelectedLegendItems } from 'types/Awair'

interface Section {
  title: string,
  name: string,
  items: Array<AwairLegendItems>
}

type Props = {
  className?: string,
  section: Section,
  selectedLegendItems: SelectedLegendItems,
  setSelectedLegendItemsCallback: (selectedLegendItem) => void
  dataAtCursor: Array<Plotly.PlotDatum> | null
}

const LegendGroup = ({ section, selectedLegendItems, setSelectedLegendItemsCallback, dataAtCursor, className = '' }: Props) => {
  const { title, name, items } = section
  const areAllSeriesSelected = _.all(items, (item) => selectedLegendItems[item.seriesName])
  const areNoSeriesSelected = _.all(items, (item) => !selectedLegendItems[item.seriesName])

  const [allSelected, setAllSelected] = useState(areAllSeriesSelected)
  const [noneSelected, setNoneSelected] = useState(areNoSeriesSelected)
  useEffect(() => {
    setAllSelected(areAllSeriesSelected)
    setNoneSelected(areNoSeriesSelected)
  }, [selectedLegendItems, areAllSeriesSelected, areNoSeriesSelected])

  const setAllSeries = useCallback((selectAll) => {
    const eventData = { category: 'Customer Detail Page', action: 'Change Awair legend option', label: selectAll ? 'All' : 'None' }

    GA4.sendEvent(eventData)

    setSelectedLegendItemsCallback((previouslySelectedLegendItems) => {
      const selectedItems = _.clone(previouslySelectedLegendItems)

      _.each(items, item => { selectedItems[item.seriesName] = selectAll })

      return selectedItems
    })
  }, [items, setSelectedLegendItemsCallback])

  return (
    <S.Section {...{ className }}>
      <S.Header>
        <S.Label>{title}</S.Label>

        {items.length > 1 && (
          <S.Controls>
            <S.ControlsLabel>Select</S.ControlsLabel>
            <S.Control selected={allSelected} onClick={() => setAllSeries(true)}>All</S.Control>
            <S.Control selected={noneSelected} onClick={() => setAllSeries(false)}>None</S.Control>
          </S.Controls>
        )}
      </S.Header>

      <S.Items>
        {_.map(items, (item) => (
          <LegendGroupItem
            key={item.seriesName}
            sectionName={name}
            data={dataAtCursor ? _.filter(dataAtCursor, point => point.data.name === item.seriesName)[0] : null}
            {...{ item, selectedLegendItems, setSelectedLegendItemsCallback }}
          />
        ))}
      </S.Items>

      {title === 'Awair Device' && (
        <S.FiveMinuteExplanation>* Awair data is based on a 5 minute average.</S.FiveMinuteExplanation>
      )}
    </S.Section>
  )
}

export default LegendGroup

const S = {
  Section: styled.div`
    width: fit-content;
    min-width: 250px;
  `,
  Header: styled.div`
    min-width: 220px;
    padding-left: 4px;
  `,
  Label: styled.div`
    display: inline-block;
    font-weight: bold;
  `,
  Controls: styled.div`
    width: fit-content;
    margin: -23px 4px 0 auto;
  `,
  ControlsLabel: styled.div`
    display: inline-block;
    margin-right: 2px;

    color: ${color('rthLegendControlsLabel')};
  `,
  Control: styled.div<{selected: boolean}>`
    display: inline-block;
    margin-left: 5px;
    padding: 2px 6px 0;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;

    color: ${({ selected }) => selected ? color('selectedRthLegendControl') : color('rthLegendControl')};
    background-color: ${({ selected }) => selected ? color('rthLegendControlBackground') : color('paneBackground')};
    cursor: ${({ selected }) => selected ? 'initial' : 'pointer'};

    &:hover {
      background-color: ${({ selected }) => selected ? color('rthLegendControlBackground') : color('rthLegendControlBackgroundHover')};
    }
  `,
  Items: styled.div`
    padding: 13px;
    border: 1px solid ${color('border')};
    border-radius: 4px;
  `,
  FiveMinuteExplanation: styled.div`
    padding-left: 5px; 
  `
}
